// Базовые типы для Digital Signage Management System

// Пользователи и роли
export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  SCHEDULER = 'scheduler',
  ENGINEER = 'engineer',
  DIRECTOR = 'director',
  VIEWER = 'viewer'
}

export interface User {
  id: number;
  username: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
  notes: string | null;
}

export interface UserCreateInput {
  username: string;
  email: string;
  password: string;
  role: UserRole;
  full_name?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  by_role: Record<string, number>;
  new_users_30d?: number;
  recent_logins_7d?: number;
  online_users?: number;
}

export interface UserActivityLog {
  id: number;
  timestamp: string;
  level: string;
  category: string;
  event_type: string;
  message: string;
  user_id: number | null;
  device_id: number | null;
  event_metadata?: Record<string, any> | null;
  ip_address?: string | null;
  user_agent?: string | null;
  related_object_type?: string | null;
  related_object_id?: number | null;
}

// Аутентификация
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<LoginResponse>;
}

// Медиафайлы
export enum MediaType {
  VIDEO = 'video',
  IMAGE = 'image', 
  AUDIO = 'audio',
  PDF = 'pdf',
  HTML = 'html'
}

export enum MediaStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  READY = 'ready',
  ERROR = 'error'
}

export interface Media {
  id: number;
  title: string;
  description: string | null;
  filename: string;
  original_filename?: string;
  file_url: string;
  thumbnail_url: string | null;
  file_size: number;
  type: MediaType;
  media_type?: MediaType;
  status: MediaStatus;
  duration: number | null;
  resolution: string | null;
  file_path?: string;
  mime_type?: string;
  file_extension?: string;
  width?: number | null;
  height?: number | null;
  tags: string[] | null;
  uploaded_by: number;
  created_at: string;
  updated_at: string;
  file_exists?: boolean;
  is_playable?: boolean;
  availability_status?: string;
  availability_message?: string | null;
}

// Устройства
export enum DeviceType {
  TV = 'tv',
  MONITOR = 'monitor',
  TABLET = 'tablet',
  LED_PANEL = 'led_panel',
  VIDEO_WALL = 'video_wall',
  RASPBERRY_PI = 'raspberry_pi',
  INTEL_NUC = 'intel_nuc',
  DESKTOP_PC = 'desktop_pc',
  ANDROID_TV = 'android_tv',
  WEB_BROWSER = 'web_browser',
  OTHER = 'other'
}

export enum DeviceStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
  ERROR = 'error',
  MAINTENANCE = 'maintenance',
  UPDATING = 'updating'
}

export enum PlaybackStatus {
  PLAYING = 'playing',
  PAUSED = 'paused',
  STOPPED = 'stopped',
  LOADING = 'loading',
  ERROR = 'error'
}

export interface SystemInfo {
  cpu_usage?: number;
  memory_usage?: number;
  storage_usage?: number;
  temperature?: number;
  uptime?: number;
  user_agent?: string;
  player?: string;
  player_kind?: string;
  client_kind?: string;
  client_label?: string;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  os_version?: string;
  platform?: string;
  language?: string;
  timezone?: string;
  online?: boolean;
  screen_width?: number;
  screen_height?: number;
  screen_resolution?: string;
  device_pixel_ratio?: number;
  hardware_concurrency?: number;
  device_memory_gb?: number;
  connection_type?: string;
  connection_downlink_mbps?: number;
  connection_rtt_ms?: number;
  touch_points?: number;
  cache_status?: {
    source?: string;
    updated_at?: string;
    total_entries?: number;
    media_entries?: number;
    total_size_bytes?: number;
    total_size?: number;
    cached_media_ids?: number[];
    items?: Array<{
      media_id?: number;
      filename?: string;
      size_bytes?: number;
      size?: number;
      cached?: boolean;
    }>;
  };
}

export interface Device {
  id: number;
  name: string;
  description: string | null;
  type: DeviceType;
  device_type?: DeviceType; // для обратной совместимости
  status: DeviceStatus;
  playback_status?: PlaybackStatus | string;
  ip_address?: string | null;
  last_ip_address?: string | null;
  mac_address?: string | null;
  location: string | null;
  floor?: string | null;
  room?: string | null;
  device_uuid?: string;
  uuid?: string; // для обратной совместимости
  hostname?: string | null;
  os_version?: string | null;
  player_version?: string | null;
  screen_resolution?: string | null;
  screen_orientation?: 'landscape' | 'portrait';
  heartbeat_interval?: number;
  is_active?: boolean;
  auto_update?: boolean;
  maintenance_mode?: boolean;
  device_settings?: Record<string, any> | null;
  
  // Геолокация
  latitude?: number | null;
  longitude?: number | null;
  country?: string | null;
  city?: string | null;
  full_address?: string | null;
  location_id?: string | null;
  last_heartbeat?: string | null;
  last_online_at?: string | null;
  current_media_id?: number | null;
  current_playlist_id?: number | null;
  current_schedule_event_id?: number | null;
  current_media?: {
    id: number;
    title: string;
    filename: string;
    original_filename?: string | null;
    media_type: MediaType;
    status: MediaStatus;
    duration?: number | null;
  } | null;
  current_playlist?: {
    id: number;
    name: string;
  } | null;
  system_info?: SystemInfo;
  signal_strength?: number;
  uptime?: number;
  version?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface DeviceCreate {
  name: string;
  description?: string;
  device_type: DeviceType;
  location?: string;
  floor?: string;
  room?: string;
  device_uuid: string;
  ip_address?: string;
  mac_address?: string;
  hostname?: string;
  os_version?: string;
  player_version?: string;
  screen_resolution?: string;
  screen_orientation?: 'landscape' | 'portrait';
  heartbeat_interval?: number;
  group_id?: number;
  device_settings?: Record<string, any>;
  
  // Геолокация
  latitude?: number;
  longitude?: number;
  country?: string;
  city?: string;
  full_address?: string;
  location_id?: string;
}

export interface DeviceUpdate {
  name?: string;
  description?: string;
  location?: string;
  floor?: string;
  room?: string;
  screen_resolution?: string;
  screen_orientation?: 'landscape' | 'portrait';
  heartbeat_interval?: number;
  is_active?: boolean;
  auto_update?: boolean;
  maintenance_mode?: boolean;
  group_id?: number;
  device_settings?: Record<string, any>;
  
  // Геолокация
  latitude?: number;
  longitude?: number;
  country?: string;
  city?: string;
  full_address?: string;
  location_id?: string;
}

// ============ GEOLOCATION TYPES ============

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface AddressInfo {
  country?: string;
  city?: string;
  full_address?: string;
  location_id?: string;
}

export interface GeoLocation extends Coordinates, AddressInfo {}

export interface MapMarker {
  id: number;
  name: string;
  coordinates: Coordinates;
  device?: Device;
  tooltip?: string;
}

export interface MapBounds {
  northeast: Coordinates;
  southwest: Coordinates;
}

export interface MapConfig {
  center: Coordinates;
  zoom: number;
  bounds?: MapBounds;
}

export interface DeviceGroup {
  id: number;
  name: string;
  description: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  devices?: Device[];
  devices_count?: number;
  online_devices_count?: number;
}

export interface AssignmentDeviceRef {
  id: number;
  name: string;
  status: string;
  playback_status?: string;
  device_type?: string;
  group_id?: number | null;
  current_playlist_id?: number | null;
  current_media_id?: number | null;
  current_schedule_event_id?: number | null;
  last_heartbeat?: string | null;
}

export interface AssignmentScheduleEventRef {
  id: number;
  name: string;
  schedule_id: number;
  schedule_name?: string | null;
  playlist_id?: number | null;
  status: string;
  priority: number | string;
  start_time: string;
  end_time: string;
  is_active_now: boolean;
  target_type: 'device' | 'group' | 'none';
  target_id?: number | null;
  target_name?: string | null;
  playback_settings?: Record<string, any>;
}

export interface AssignmentManualCommandRef {
  id: number;
  device_id: number;
  device_name?: string | null;
  playlist_id?: number | null;
  playlist_name?: string | null;
  status: string;
  created_at: string;
  executed_at?: string | null;
  source?: string | null;
}

export interface AssignmentPlaylistRow {
  id: number;
  name: string;
  status?: string | null;
  type?: string | null;
  items_count: number;
  total_duration?: number | null;
  is_public?: boolean;
  scheduled_events: AssignmentScheduleEventRef[];
  target_devices: AssignmentDeviceRef[];
  target_groups: Array<{ id: number; name: string; devices_count: number }>;
  current_devices: AssignmentDeviceRef[];
  recent_manual_commands: AssignmentManualCommandRef[];
}

export interface AssignmentDeviceRow extends AssignmentDeviceRef {
  group_name?: string | null;
  current_playlist_name?: string | null;
  current_media_title?: string | null;
  current_schedule_event_name?: string | null;
  scheduled_playlists: Array<{ id: number; name: string; status?: string | null; active_now: boolean }>;
  scheduled_events: AssignmentScheduleEventRef[];
  latest_manual_playlist_command?: AssignmentManualCommandRef | null;
}

export interface AssignmentsOverview {
  generated_at: string;
  summary: {
    playlists_total: number;
    playlists_scheduled: number;
    devices_total: number;
    devices_playing: number;
    groups_total: number;
    schedule_events_total: number;
  };
  playlists: AssignmentPlaylistRow[];
  devices: AssignmentDeviceRow[];
}

// Плейлисты
export enum PlaylistStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  ARCHIVED = 'archived'
}

export enum PlaylistType {
  STANDARD = 'standard',
  EMERGENCY = 'emergency',
  TEMPLATE = 'template'
}

export interface PlaylistItem {
  id: number;
  playlist_id: number;
  media_id: number;
  order: number;
  order_index?: number;
  duration_override: number | null; // Переопределение длительности
  custom_duration?: number | null;
  transition_type: string | null;
  transition_duration: number;
  start_date: string | null;
  end_date: string | null;
  start_time?: number | null;
  end_time?: number | null;
  volume?: number | null;
  fit_mode?: 'stretch' | 'fit' | 'fill' | 'crop';
  created_at: string;
  media?: Media;
  is_playable?: boolean;
  availability_status?: string;
  availability_message?: string | null;
}

export interface Playlist {
  id: number;
  name: string;
  description: string | null;
  status: PlaylistStatus;
  type: PlaylistType;
  is_default: boolean;
  loop_enabled: boolean;
  shuffle_enabled: boolean;
  items_count: number;
  total_duration: number; // В секундах
  created_by: number;
  created_at: string;
  updated_at: string;
  last_played?: string | null;
  devices_count?: number; // @deprecated Используйте события расписания для связи с устройствами
  items?: PlaylistItem[];
  creator?: User;
  preview_media?: Media[]; // Первые 3-4 медиафайла для превью
}

// Расписание - синхронизация с backend
export enum ScheduleType {
  ONE_TIME = 'one_time',
  DAILY = 'daily', 
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  CUSTOM = 'custom'
}

export enum ScheduleStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled', 
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  CONFLICT = 'conflict'
}

export enum EventStatus {
  SCHEDULED = 'scheduled',
  ACTIVE = 'active', 
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  SKIPPED = 'skipped'
}

export enum Priority {
  LOW = 1,
  NORMAL = 5,
  HIGH = 10,
  URGENT = 15,
  CRITICAL = 20
}

export enum RecurrenceType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  CUSTOM = 'custom'
}

export interface RecurrencePattern {
  type: RecurrenceType;
  interval: number; // Каждые N дней/недель/месяцев
  days_of_week?: number[]; // 0=Sunday, 1=Monday, etc.
  days_of_month?: number[]; // 1-31
  months?: number[]; // 1-12
  end_date?: string | null;
  occurrence_count?: number | null; // Количество повторений
}

// Основная модель расписания (соответствует backend Schedule)
export interface Schedule {
  id: number;
  name: string;
  description: string | null;
  schedule_type: ScheduleType;
  repeat_config: any | null; // JSON конфигурация повторения
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at: string | null;
  // Связанные данные
  creator?: User;
  events?: ScheduleEvent[];
  events_count?: number;
  active_events_count?: number;
}

// Событие расписания (соответствует backend ScheduleEvent)
export interface ScheduleEvent {
  id: number;
  schedule_id: number;
  name: string;
  title?: string;
  description: string | null;
  start_time: string;
  end_time: string;
  priority: Priority;
  device_id: number | null;
  device_group_id: number | null;
  playlist_id: number | null;
  media_id: number | null;
  status: EventStatus;
  playback_settings: any | null;
  execution_result: any | null;
  error_message: string | null;
  created_at: string;
  updated_at: string | null;
  executed_at: string | null;
  // Связанные данные
  schedule?: Schedule;
  device?: Device;
  device_group?: DeviceGroup;
  playlist?: Playlist;
  media?: Media;
  // Вычисляемые поля
  duration_minutes?: number;
  is_active_now?: boolean;
  is_scheduled_for_today?: boolean;
  target_description?: string;
  content_description?: string;
}

// Расширенные типы для UI
export interface ScheduleWithEvents extends Schedule {
  events: ScheduleEvent[];
}

export interface ScheduleEventWithDetails extends ScheduleEvent {
  schedule: Schedule;
  device?: Device;
  device_group?: DeviceGroup;
  playlist?: Playlist;
  media?: Media;
}

export interface ScheduleConflict {
  conflict_event_id: number;
  conflict_event_name: string;
  conflict_start_time: string;
  conflict_end_time: string;
  conflict_device_id: number | null;
  conflict_device_name: string | null;
  conflict_group_id: number | null;
  conflict_group_name: string | null;
}

export interface ScheduleStats {
  total_schedules: number;
  active_schedules: number;
  total_events: number;
  scheduled_events: number;
  active_events: number;
  completed_events: number;
  failed_events: number;
  by_type: Record<ScheduleType, number>;
  by_priority: Record<Priority, number>;
  upcoming_events: ScheduleEvent[];
}

export interface CalendarEvent {
  id: number;
  title: string;
  start_time: string;
  end_time: string;
  status: EventStatus;
  priority: Priority;
  device?: Device;
  playlist?: Playlist;
  media?: Media;
  author?: User;
  description?: string;
}

// Схемы для создания и обновления
export interface ScheduleCreate {
  name: string;
  description?: string;
  schedule_type: ScheduleType;
  repeat_config?: any;
  start_date: string;
  end_date?: string;
  is_active?: boolean;
}

export interface ScheduleUpdate {
  name?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  is_active?: boolean;
  repeat_config?: any;
}

export interface ScheduleEventCreate {
  schedule_id?: number; // Опционально для endpoint /{schedule_id}/events, обязательно для /events
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  priority?: number; // 1=LOW, 5=NORMAL, 10=HIGH, 15=URGENT, 20=CRITICAL
  device_id?: number | null;
  device_group_id?: number | null;
  playlist_id?: number | null;
  media_id?: number | null;
  playback_settings?: any;
}

export interface ScheduleEventUpdate {
  name?: string;
  description?: string;
  start_time?: string;
  end_time?: string;
  priority?: number; // 1=LOW, 5=NORMAL, 10=HIGH, 15=URGENT, 20=CRITICAL
  device_id?: number;
  device_group_id?: number;
  playlist_id?: number;
  media_id?: number;
  playback_settings?: any;
}

// API ответы
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ApiError {
  detail: string;
  code?: string;
  field?: string;
}

// Дашборд
export interface DashboardStats {
  total_devices: number;
  online_devices: number;
  offline_devices?: number;
  active_playback_devices?: number;
  online_percentage?: number;
  total_media: number;
  total_playlists: number;
  playlist_items?: number;
  active_schedules: number;
  storage_used: number;
  storage_total: number;
  storage_usage_percentage?: number;
  events_24h?: number;
  uploads_24h?: number;
  playbacks_24h?: number;
  playbacks_7d?: number;
  playback_duration_24h?: number;
  playback_completion_rate_24h?: number;
  playback_device_coverage_24h?: number;
  content_rotation_coverage_24h?: number;
  playlist_rotation_coverage_24h?: number;
  interrupted_playbacks_24h?: number;
  unique_playback_devices_24h?: number;
  unique_media_played_24h?: number;
  unique_playlists_played_24h?: number;
  errors_24h?: number;
  warnings_24h?: number;
  error_rate_24h?: number;
  commands_24h?: number;
  failed_commands_24h?: number;
  pending_commands_24h?: number;
  command_success_rate_24h?: number;
  command_backlog_rate_24h?: number;
  stale_devices_count?: number;
  playlist_average_items?: number;
  health_score?: number;
  most_played_media?: Array<{
    content_id: number;
    content_name: string;
    content_type: string;
    total_plays: number;
    total_duration: number;
    completion_rate: number;
    average_duration: number;
    unique_devices: number;
  }>;
  most_used_playlists?: Array<{
    content_id: number;
    content_name: string;
    content_type: string;
    total_plays: number;
    total_duration: number;
    completion_rate: number;
    average_duration: number;
    unique_devices: number;
  }>;
  problematic_devices?: Array<{
    device_id: number;
    device_name: string;
    uptime_percentage: number;
    total_hours: number;
    downtime_hours: number;
    last_online?: string | null;
    status_changes: number;
    device_status?: string | null;
    playback_status?: string | null;
    last_heartbeat?: string | null;
    online_events?: number;
    offline_events?: number;
  }>;
  stale_devices?: Array<{
    id: number;
    name: string;
    status: string;
    playback_status?: string | null;
    last_heartbeat?: string | null;
    client_kind?: string | null;
  }>;
  hourly_activity?: Record<string | number, number>;
  device_status_distribution?: Record<string, number>;
  device_type_distribution?: Record<string, number>;
  media_status_distribution?: Record<string, number>;
  media_type_distribution?: Record<string, number>;
  event_level_distribution?: Record<string, number>;
  event_category_distribution?: Record<string, number>;
  command_status_distribution?: Record<string, number>;
  recent_events?: Array<{
    id: number;
    timestamp: string;
    level: string;
    category: string;
    event_type: string;
    message: string;
    user_id?: number | null;
    device_id?: number | null;
    device_name?: string | null;
    event_metadata?: Record<string, unknown> | null;
    ip_address?: string | null;
    user_agent?: string | null;
  }>;
}

// Фильтры и поиск
export interface SearchFilters {
  search?: string;
  mediaTypes?: string[];
  statuses?: string[];
  deviceStatuses?: string[];
  deviceTypes?: string[];
  playlistStatuses?: string[];
  playlistTypes?: string[];
  scheduleStatuses?: ScheduleStatus[];
  scheduleTypes?: ScheduleType[];
  eventStatuses?: EventStatus[];
  priorities?: Priority[];
  userRoles?: string[];
  locations?: string[];
  tags?: string[];
  fileSizeRange?: [number, number];
  durationRange?: [number, number];
  onlineOnly?: boolean;
  offlineOnly?: boolean;
  activeOnly?: boolean;
  activeUsersOnly?: boolean;
  inactiveUsersOnly?: boolean;
  onlineUsersOnly?: boolean;
  recurringOnly?: boolean;
  conflictsOnly?: boolean;
  createdBy?: number;
  playlistId?: number;
  deviceId?: number;
  deviceGroupId?: number;
  scheduleId?: number;
  dateFrom?: string;
  dateTo?: string;
  timeFrom?: string;
  timeTo?: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

// Уведомления
export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error'
}

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

// Состояние приложения
export interface AppState {
  isLoading: boolean;
  notifications: Notification[];
  theme: 'light' | 'dark';
  language: 'ru' | 'en';
}

// Browser web-player
export interface WebPlayerCreate {
  name: string;
  description?: string | null;
  location?: string | null;
  screen_resolution?: string | null;
  screen_orientation?: 'landscape' | 'portrait';
  group_id?: number | null;
  expires_at?: string | null;
}

export interface WebPlayerLink {
  id: number;
  device_id: number;
  token_prefix: string;
  name: string;
  description: string | null;
  is_active: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  last_used_at: string | null;
  device?: Device | null;
}

export interface WebPlayerCreated extends WebPlayerLink {
  token: string;
  player_url: string;
}

export interface WebPlayerDeviceState {
  id: number;
  name: string;
  device_uuid: string;
  device_type: string;
  status: string;
  playback_status: string;
  current_media_id: number | null;
  current_playlist_id: number | null;
  current_schedule_event_id: number | null;
  screen_resolution?: string | null;
  screen_orientation?: string | null;
}

export interface WebPlayerBootstrap {
  device: WebPlayerDeviceState;
  server_time: string;
  media_base_url: string;
  command_poll_interval_seconds: number;
  heartbeat_interval_seconds: number;
}

export interface WebPlayerCommand {
  id: number;
  command_type: string;
  command_data: Record<string, any>;
  created_at: string;
}

export interface PlaybackSyncCreate {
  playlist_id: number;
  device_ids?: number[];
  group_ids?: number[];
  schedule_event_id?: number | null;
  start_delay_seconds?: number;
  sync_tolerance_ms?: number;
  drift_correction_threshold_ms?: number;
  loop?: boolean | null;
}

export interface PlaybackSyncDevice {
  id: number;
  sync_session_id: number;
  device_id: number;
  command_id?: number | null;
  status: string;
  last_reported_media_id?: number | null;
  last_playlist_item_id?: number | null;
  timeline_position_ms?: number | null;
  drift_ms?: number | null;
  clock_offset_ms?: number | null;
  last_heartbeat_at?: string | null;
  error_message?: string | null;
  device_name?: string | null;
}

export interface PlaybackSyncSession {
  id: number;
  playlist_id: number;
  playlist_name?: string | null;
  schedule_event_id?: number | null;
  device_group_id?: number | null;
  created_by_user_id?: number | null;
  status: string;
  start_at: string;
  preload_deadline_at: string;
  server_time_issued_at: string;
  loop: boolean;
  shuffle: boolean;
  sync_tolerance_ms: number;
  drift_correction_threshold_ms: number;
  metadata?: Record<string, any> | null;
  created_at?: string | null;
  updated_at?: string | null;
  ended_at?: string | null;
  devices: PlaybackSyncDevice[];
}

// Навигация
export interface NavigationItem {
  label: string;
  path: string;
  icon: React.ComponentType;
  roles?: UserRole[];
  children?: NavigationItem[];
}

// Формы
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'select' | 'textarea';
  required?: boolean;
  options?: { value: string; label: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
}

// Таблицы
export interface TableColumn {
  id: string;
  label: string;
  sortable?: boolean;
  width?: number;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string;
}

export interface TableProps {
  columns: TableColumn[];
  data: any[];
  loading?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onRowClick?: (row: any) => void;
}

// Календарь 
export interface CalendarView {
  name: 'month' | 'week' | 'day' | 'agenda';
  title: string;
}

// Метрики устройств  
export interface DeviceMetrics {
  device_id: number;
  device_name: string;
  cpu_usage: number;
  memory_usage: number;
  storage_usage: number;
  network_speed: number;
  temperature: number;
  uptime_percentage: number;
  last_heartbeat: string;
  current_playlist: string | null;
  errors_count: number;
  timestamp: string;
} 

export interface DeviceAvailabilityEvent {
  id: number;
  timestamp: string;
  event_type: string;
  level?: string;
  message: string;
}

export interface DeviceAvailabilityInterval {
  type: 'online' | 'offline' | string;
  label: string;
  started_at: string;
  ended_at: string;
  duration_seconds: number;
}

export interface DeviceAvailabilityPlayback {
  id: number;
  media_id?: number | null;
  media_title?: string | null;
  playlist_id?: number | null;
  playlist_name?: string | null;
  schedule_event_id?: number | null;
  started_at: string;
  finished_at?: string | null;
  duration_actual?: number | null;
  duration_planned?: number | null;
  completed: boolean;
  interrupted: boolean;
  playback_data?: Record<string, any>;
}

export interface DeviceAvailabilityDay {
  date: string;
  status: 'online' | 'partial' | 'offline' | 'no_data';
  uptime_percentage: number;
  online_seconds: number;
  offline_seconds: number;
  playback_seconds: number;
  proof_count: number;
  completed_count: number;
  interrupted_count: number;
  unique_media_count: number;
  unique_playlist_count: number;
  media_titles: string[];
  playlist_names: string[];
  events: DeviceAvailabilityEvent[];
  intervals: DeviceAvailabilityInterval[];
  playbacks: DeviceAvailabilityPlayback[];
}

export interface DeviceAvailabilityCalendar {
  device: {
    id: number;
    name: string;
    status: string;
    playback_status?: string | null;
    last_heartbeat?: string | null;
    heartbeat_interval?: number | null;
    ip_address?: string | null;
    os_version?: string | null;
  };
  period: {
    date_from: string;
    date_to: string;
    timezone: string;
    started_at: string;
    ended_at: string;
  };
  summary: {
    online_seconds: number;
    offline_seconds: number;
    playback_seconds: number;
    proof_count: number;
    completed_count: number;
    interrupted_count: number;
    days_total: number;
    days_online: number;
    days_partial: number;
    days_offline: number;
    days_no_data: number;
    uptime_percentage: number;
  };
  days: DeviceAvailabilityDay[];
}
